import type { SessionUser } from '@afra/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { RequestError, api } from '../../lib/api'

/** Whether this session may archive the conversation. The server checks the same thing. */
export function canArchiveChat(user: SessionUser): boolean {
  return !user.impersonating && user.role !== 'member'
}

/**
 * «بایگانی و شروع تازه» — and the confirmation it opens.
 *
 * Nothing is deleted, so this is an ordinary button, not a red one: the old
 * conversation keeps everything it produced and stays readable under
 * «گفت‌وگوهای قبلی». The one real choice is whether unfinished tasks follow
 * the director into the new conversation.
 */
export function ArchiveChat({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [carryOpenTasks, setCarryOpenTasks] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const open = () => {
    setCarryOpenTasks(true)
    setError(null)
    dialogRef.current?.showModal()
  }
  const close = () => dialogRef.current?.close()

  const confirm = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.archiveChat(carryOpenTasks)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chat'] }),
        queryClient.invalidateQueries({ queryKey: ['chat-context'] }),
        queryClient.invalidateQueries({ queryKey: ['chat-archive'] }),
      ])
      close()
    } catch (err) {
      setError(err instanceof RequestError ? err.messageFa : 'بایگانی انجام نشد. دوباره امتحان کنید.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button type="button" className="btn btn--ghost btn--sm" onClick={open}>
        بایگانی و شروع تازه
      </button>

      <dialog
        ref={dialogRef}
        className="dialog"
        aria-labelledby="archive-title"
        onClick={(e) => {
          if (e.target === e.currentTarget && !busy) close()
        }}
      >
        <div className="dialog__head">
          <span id="archive-title">بایگانی این گفت‌وگو</span>
          <button type="button" className="btn btn--quiet btn--sm" onClick={close} disabled={busy} aria-label="بستن">
            ×
          </button>
        </div>

        <div className="dialog__body">
          <p>
            این گفت‌وگو با همهٔ پیام‌ها، چالش و نتیجهٔ مطلوب، حدس‌ها، نکته‌ها و کارهایش بایگانی می‌شود و یک
            گفت‌وگوی تازه شروع می‌شود. چیزی پاک نمی‌شود و هر وقت بخواهید آن را در «گفت‌وگوهای قبلی» می‌بینید.
          </p>
          <p className="meta">
            مشخصات مؤسسه و نمره‌های بلوغ مال خود مؤسسه‌اند و در گفت‌وگوی تازه هم سر جایشان می‌مانند.{' '}
            {user.personalSpace
              ? 'این فقط فضای شخصی شماست و به هیچ مؤسسه‌ای ربطی ندارد.'
              : 'این گفت‌وگو بین همهٔ اعضای مؤسسه مشترک است.'}
          </p>

          <label className="check" style={{ marginBlock: 'var(--space-3)' }}>
            <input
              type="checkbox"
              checked={carryOpenTasks}
              disabled={busy}
              onChange={(e) => setCarryOpenTasks(e.target.checked)}
            />
            <span>کارهای باز را به گفت‌وگوی تازه ببر</span>
            <span className="check__hint">
              کارهایی که هنوز انجام نشده‌اند در ستون گفت‌وگوی تازه هم دیده می‌شوند. اگر تیک را بردارید، همراه
              گفت‌وگوی قبلی بایگانی می‌شوند.
            </span>
          </label>

          {error && <div className="notice notice--error">{error}</div>}
        </div>

        <div className="dialog__foot">
          <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>
            انصراف
          </button>
          <button type="button" className="btn" onClick={confirm} disabled={busy}>
            {busy ? 'در حال بایگانی…' : 'بایگانی و شروع گفت‌وگوی تازه'}
          </button>
        </div>
      </dialog>
    </>
  )
}
